import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, Box } from '@material-ui/core';
import Chart from 'react-apexcharts';
import { COLORS } from '../../constants';
import { useCardStyles } from '../../styles';


/**
 * Componente de gráfica de dona para visualizar distribución de usuarios
 * Muestra usuarios medidos vs usuarios sin mediciones en formato de dona
 * @param {Object} props - Props del componente
 * @param {Object} props.data - Datos para la gráfica
 * @param {number} props.data.measuredUsers - Cantidad de usuarios medidos
 * @param {number} props.data.usersWithoutMeasurements - Cantidad de usuarios sin mediciones
 * @param {string} props.month - Mes actual
 * @param {number|string} props.year - Año actual
 * @returns {JSX.Element} Gráfica de dona con distribución de usuarios
 */
export default function DonutChart({ 
    data = {},
    month = '',
    year = '',
}) {
    const classes = useCardStyles();

    const {
        measuredUsers = 0,
        usersWithoutMeasurements = 0,
    } = data;

    const periodLabel = month && year ? `${month} ${year}` : 'Sin período';

    // Optimizar configuración del gráfico con useMemo
    const chartOptions = useMemo(() => ({
        chart: {
            type: 'donut',
        },
        labels: ['Usuarios medidos', 'Sin medición'],
        colors: [COLORS.primary, '#FF5722'],
        legend: {
            position: 'bottom',
            fontSize: '14px',
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return Math.round(val) + '%';
            },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: '16px',
                            fontWeight: 600,
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                            },
                        },
                    },
                },
            },
        },
        responsive: [{
            breakpoint: 480,
            options: {
                chart: {
                    width: 250,
                },
                legend: {
                    position: 'bottom',
                },
            },
        }],
    }), []);

    const chartSeries = useMemo(() => 
        [measuredUsers, usersWithoutMeasurements],
        [measuredUsers, usersWithoutMeasurements]
    );

    return (
        <Paper className={classes.card} style={{ alignItems: 'center' }}>
            <Typography className={classes.title} style={{ alignSelf: 'flex-start' }}>
                Distribución de Usuarios
            </Typography>
            <Typography className={classes.subtitle} style={{ alignSelf: 'flex-start', marginBottom: '16px' }}>
                {periodLabel}
            </Typography>
            <Box style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Chart 
                    options={chartOptions} 
                    series={chartSeries} 
                    type="donut" 
                    width="280"
                />
            </Box>
        </Paper>
    );
}

DonutChart.propTypes = {
    data: PropTypes.shape({
        measuredUsers: PropTypes.number.isRequired,
        usersWithoutMeasurements: PropTypes.number.isRequired,
    }).isRequired,
    month: PropTypes.string.isRequired,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
